import { useEffect, useState } from 'react';
import { fetchDictionaries } from '../services/DictionaryService';
import { confirmLoginGrant, getReauthErrorKey, REAUTH_ERRORS, requestLoginGrant } from '../services/ReauthService';
import { getSubject } from '../utils/helpers/jwt';
import { cancelReauth, completeReauth, registerReauthHost } from '../utils/helpers/sessionManager';
import { getAccessToken, markActivity } from '../utils/helpers/sessionStore';
import { getOrgId } from '../utils/helpers/storage';
import useReauthReason from './useReauthReason';

export const REAUTH_STEPS = {
  password: 'password',
  otp: 'otp'
};

const hasKnownUser = () => Boolean(getSubject(getAccessToken()));

const abortReauthIf = (shouldAbort) => {
  if (shouldAbort) cancelReauth();
  return shouldAbort;
};

const finishLogin = async (grant, otp) => {
  const orgId = getOrgId();
  if (abortReauthIf(orgId == null)) return;

  const tokens = await confirmLoginGrant(grant, orgId, otp);
  markActivity();
  completeReauth(tokens);
  fetchDictionaries({ force: true }).catch(() => {});
};

const useReauth = () => {
  const [step, setStep] = useState(REAUTH_STEPS.password);
  const [grant, setGrant] = useState(null);
  const [errorKey, setErrorKey] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reason = useReauthReason();

  useEffect(() => registerReauthHost(), []);

  useEffect(() => {
    if (reason) return;

    setStep(REAUTH_STEPS.password);
    setGrant(null);
    setErrorKey('');
  }, [reason]);

  const runStep = async (action, fallbackKey) => {
    setErrorKey('');
    setIsSubmitting(true);
    try {
      await action();
    } catch (error) {
      setErrorKey(getReauthErrorKey(error, fallbackKey));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = (credentials) =>
    runStep(async () => {
      if (abortReauthIf(!hasKnownUser())) return;

      const newGrant = await requestLoginGrant(credentials);
      if (abortReauthIf(!newGrant.sameUser)) return;

      if (newGrant.needsOtp) {
        setGrant(newGrant);
        setStep(REAUTH_STEPS.otp);
        return;
      }

      await finishLogin(newGrant, null);
    }, REAUTH_ERRORS.badCredentials);

  const handleOtpSubmit = (otp) => runStep(() => finishLogin(grant, otp), REAUTH_ERRORS.invalidCode);

  const handleLeave = () => cancelReauth();

  return { reason, step, errorKey, isSubmitting, handlePasswordSubmit, handleOtpSubmit, handleLeave };
};

export default useReauth;
